import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { HeartPlus } from "lucide-react";
import { handleMultiEmotion } from "@/screens/StudentData/handlers/multiEmotionHandler";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { filterStudentsByAlerts } from "../../utils/alertFilter";
import NoData from "../../components/NoData";
import isEqual from "lodash.isequal";
import { useDeepCompareMemo } from "use-deep-compare";
import StudentCard from "./StudentCard";
import StudentCardSkeleton from "./StudentCardSkeleton";
import useMultiEmotionWorker from "@/hooks/useMultiEmotionWorker";
import { sortStudentList } from "@/utils/helperFunctions";
import { List } from "react-window";

dayjs.extend(relativeTime);

function buildStateForWorker(state) {
  return {
    chosenEmotion: state.chosenEmotion,
    chosenStudent: state.chosenStudent,
    chosenClass: state.chosenClass,
    chosenSchool: state.chosenSchool,
    chosenDistrict: state.chosenDistrict,
    selectedTimes: state.selectedTimes,
    selectedSubOptions: state.selectedSubOptions,
    helpfulFilter: state.helpfulFilter,
    chosenPercentage: state.chosenPercentage,
    districts: state.districts,
    studentData: state.studentData,
    filterAmountCurrent: state.filterAmountCurrent.valueOf(),
    filterAmountCompare: state.filterAmountCompare.valueOf(),
  };
}

const SMALL_LIST_THRESHOLD = 20;

const StudentListHeader = () => {
  return (
    <div className="sticky top-0 z-10 bg-[#F4F4F3] hidden sm:block relative before:content-[''] before:absolute before:-top-2 before:left-0 before:right-0 before:h-2 before:bg-[#F4F4F3] before:pointer-events-none">
      <div className="bg-white rounded-t-lg border border-[#DDDDDD] overflow-hidden">
        <div className="px-4 py-3 2xl:pl-7 2xl:pr-2 flex items-center justify-between gap-3">
          <div className="grid grid-cols-4 items-center gap-3 w-full min-w-0 text-[15px]">
            <div className="col-span-1 w-full min-w-0">
              <span className=" font-semibold uppercase tracking-wide whitespace-normal break-words leading-snug">
                Student
              </span>
            </div>
            <div className="col-span-1 w-full min-w-0">
              <span className="font-semibold uppercase tracking-wide whitespace-normal break-words leading-snug">
                Tier 2/3 MTSS
              </span>
            </div>
            <div className="col-span-1 w-full min-w-0">
              <span className="font-semibold uppercase tracking-wide whitespace-normal break-words leading-snug">
                Last Emotion
              </span>
            </div>
            <div className="col-span-1 w-full min-w-0">
              <span className="font-semibold uppercase tracking-wide whitespace-normal break-words leading-snug">
                Emotion Pattern
              </span>
            </div>
          </div>
          {/* reserve space for the row chevron so header columns align */}
          <div className="w-6 h-6 flex-shrink-0" />
        </div>
      </div>
    </div>
  );
};

const StudentList = ({
  state,
  onStudentClick,
  selectedStudent,
  formattedEmotions,
  allEmotions,
  alerts,
  pinnedStudents: pinnedStudentsFromHook,
  togglePinnedStudent,
  enableAutoEmotionUpdate = true,
  isModal = false,
  onFilteredStudentsChange,
  listRef,
  isModalOpen,
}) => {
  const [hovered, setHovered] = useState(null);
  const [workerResults, setWorkerResult] = useState({});
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024,
  );
  const baseStateRef = useRef(buildStateForWorker(state));
  const workerCacheRef = useRef({});
  const prevEmotions = useRef(null);
  const { computeEmotion, workerAvailable } = useMultiEmotionWorker();
  const {
    chosenClass,
    filteredDataPoints,
    studentSupportData,
    studentEmailToNameMap,
    chosenStudent,
  } = state;

  useEffect(() => {
    const newBaseState = buildStateForWorker(state);
    if (!isEqual(newBaseState, baseStateRef.current)) {
      baseStateRef.current = newBaseState;
      workerCacheRef.current = {};
    }
  }, [state]);

  const runWorkerForEmotion = useCallback(
    async (emotion) => {
      const cacheValue = workerCacheRef.current[emotion];

      if (cacheValue && cacheValue !== "__PENDING__") {
        return;
      }

      if (cacheValue === "__PENDING__") {
        return;
      }

      const safeState = baseStateRef.current;

      const updateResult = (mapObj) => {
        workerCacheRef.current[mapObj.emotion] = mapObj?.studentSupportData;
        setWorkerResult((prev) => ({
          ...prev,
          [mapObj.emotion]: mapObj?.studentSupportData,
        }));
      };

      const runFallback = () => {
        try {
          const result = handleMultiEmotion(safeState, [emotion]) || {};

          const configResult = {
            emotion,
            studentSupportData: result.studentSupportData,
          };
          updateResult(configResult);
        } catch (err) {
          updateResult({});
        }
      };

      if (workerAvailable) {
        workerCacheRef.current[emotion] = "__PENDING__";
        try {
          const result = await computeEmotion(emotion, safeState);
          updateResult(result);
          return;
        } catch (err) {
          runFallback();
          return;
        }
      }

      // runFallback();
    },
    [workerAvailable, computeEmotion],
  );

  useEffect(() => {
    if (!enableAutoEmotionUpdate) {
      return;
    }

    const emotions = state.multiEmotion?.emotions;

    if (!Array.isArray(emotions)) {
      return;
    }

    if (!isEqual(state, prevEmotions.current)) {
      prevEmotions.current = state;
      emotions.forEach(runWorkerForEmotion);
    }

    return () => {
      prevEmotions.current = null;
    };
  }, [state, enableAutoEmotionUpdate, runWorkerForEmotion]);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleStudentClick = useCallback(
    (email) => {
      if (listRef?.current) {
        listRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
      if (typeof onStudentClick === "function") {
        onStudentClick(email);
      }
    },
    [listRef, onStudentClick],
  );

  const lastEmotionMap = useDeepCompareMemo(() => {
    const map = new Map();

    const combinedData = [];
    Array.prototype.push.apply(combinedData, filteredDataPoints);

    for (const entry of combinedData) {
      const { email, emotion, date } = entry;
      const time = typeof date === "number" ? date : new Date(date).getTime();
      const existing = map.get(email);
      if (!existing || time > existing.time) {
        map.set(email, { emotion, date, time });
      }
    }

    return Object.fromEntries(map);
  }, [filteredDataPoints]);

  const studentFlags = useDeepCompareMemo(() => {
    const flags = {};
    if (!alerts || !Array.isArray(alerts)) {
      return flags;
    }

    alerts.forEach((alert) => {
      const email = alert?.student?.email;
      if (!email) {
        return;
      }
      const { level, screenerType } = alert?.highestTier;

      if (!level) {
        return;
      }

      flags[email] = {
        level: Math.max(flags[email]?.level ?? 1, level),
        screenerType,
      };
    });
    return flags;
  }, [alerts]);

  const LEVEL_TO_FLAG = useMemo(() => {
    return {
      3: "red",
      2: "yellow",
      1: "gray",
    };
  }, []);

  const FLAG_COLORS = {
    red: {
      border: "border-l-4 border-l-[#C53030]",
      bg: "bg-[#FFEFF0]",
    },
    yellow: {
      border: "border-l-4 border-l-[#E09C5C]",
      bg: "bg-[#FFF5EB]",
    },
    gray: {
      border: "border-l-4 border-l-[#94A3B8]",
      bg: "bg-white",
    },
  };

  const FLAG_PRIORITY = { red: 0, yellow: 1, gray: 2 };

  const filteredStudents = useDeepCompareMemo(() => {
    if (!alerts || !Array.isArray(alerts) || !studentSupportData.length) {
      return [];
    }
    const students = [];
    const seenEmails = new Set();

    const latestAlertMap = new Map();
    for (const alert of alerts) {
      const email = alert.student?.email;

      if (!email) {
        continue;
      }

      const alertsList = alert.alerts;
      if (!alertsList?.length) {
        continue;
      }

      let latest = alertsList[0];

      for (let i = 1; i < alertsList.length; i++) {
        if (alertsList[i].createdAt > latest.createdAt) {
          latest = alertsList[i];
        }
      }

      latestAlertMap.set(email, latest);
    }

    const processStudent = (s) => {
      if (!lastEmotionMap[s.name] || seenEmails.has(s.name)) {
        return;
      }
      seenEmails.add(s.name);
      const latestAlert = latestAlertMap.get(s.name);
      let latestAlertStat;

      if (latestAlert?.type?.kind === "emotion") {
        const emotionMap = workerResults[latestAlert.wellness.emotion] || {};
        if (Array.isArray(emotionMap)) {
          latestAlertStat = emotionMap.find((entry) => entry.name === s.name);
        }
      }

      students.push({
        name: s.name,
        count: s.count || 0,
        total: s.total || 0,
        percent: s.total ? (s.count / s.total) * 100 : 0,
        actions: s.actions || {},
        latestAlert,
        latestAlertStat,
      });
    };

    if (chosenStudent !== "All Students" && !isModal) {
      const selectedData = studentSupportData?.find(
        (s) => s.name === chosenStudent,
      );

      if (selectedData && lastEmotionMap[selectedData.name]) {
        processStudent(selectedData);
        return students;
      }
    }

    (state.studentSupportData || []).forEach(processStudent);

    let filtered = filterStudentsByAlerts(
      students,
      alerts,
      state.chosenAlertType,
      state.chosenAlertStatus,
    );
    filtered = filtered.filter((s) => lastEmotionMap[s.name]);

    return sortStudentList(
      filtered,
      pinnedStudentsFromHook,
      null,
      LEVEL_TO_FLAG,
      FLAG_PRIORITY,
      selectedStudent,
      studentFlags,
    );
  }, [
    state.studentSupportData,
    alerts,
    state.chosenStudent,
    selectedStudent,
    pinnedStudentsFromHook,
    lastEmotionMap,
    studentFlags,
    isModal,
    LEVEL_TO_FLAG,
    FLAG_PRIORITY,
    workerResults,
  ]);

  useEffect(() => {
    if (onFilteredStudentsChange) {
      onFilteredStudentsChange(filteredStudents.length);
    }
  }, [filteredStudents, onFilteredStudentsChange]);

  const togglePin = useCallback(
    async (email, e) => {
      e.stopPropagation();
      const pinnedStudent = pinnedStudentsFromHook.find(
        (pinned) => pinned.student?.email === email,
      );

      if (pinnedStudent) {
        await togglePinnedStudent(pinnedStudent.student?.email);
      } else {
        await togglePinnedStudent(email);
      }
    },
    [pinnedStudentsFromHook, togglePinnedStudent],
  );

  const displayStudents = useDeepCompareMemo(() => {
    return filteredStudents.map((student) => {
      const email = student.name;
      const level = studentFlags[email]?.level ?? 1;
      const screenerType = studentFlags[email]?.screenerType ?? null;
      const flag = LEVEL_TO_FLAG[level];
      const { border, bg } = FLAG_COLORS[flag] || {};
      const name = studentEmailToNameMap[email] || email;
      const percent = Number((student.percent || 0).toFixed(1));
      const last = lastEmotionMap[email];
      const lastEmotion = last?.emotion || "";
      const timeAgo = last?.date ? dayjs(last.date).fromNow(true) : "";
      const isPinned = pinnedStudentsFromHook.some(
        (p) => p.student?.email === email,
      );

      return {
        ...student,
        email,
        level,
        screenerType,
        flag,
        border,
        bg,
        name,
        percent,
        lastEmotion,
        timeAgo,
        isPinned,
      };
    });
  }, [
    filteredStudents,
    studentFlags,
    LEVEL_TO_FLAG,
    FLAG_COLORS,
    studentEmailToNameMap,
    lastEmotionMap,
    pinnedStudentsFromHook,
  ]);

  const itemData = useMemo(
    () => ({
      students: displayStudents || [],
      selectedStudent: selectedStudent || null,
      hovered: hovered || null,
      setHovered: setHovered || (() => {}), // Safe noop if missing
      handleStudentClick: handleStudentClick || (() => {}),
      togglePin: togglePin || (() => {}),
      isModal: Boolean(isModal), // Coerce to boolean
      isModalOpen: Boolean(isModalOpen),
      chosenClass: chosenClass || "",
      formattedEmotions: formattedEmotions || {},
    }),
    [
      displayStudents,
      selectedStudent,
      hovered,
      setHovered,
      handleStudentClick,
      togglePin,
      isModal,
      isModalOpen,
      chosenClass,
      formattedEmotions,
    ],
  );

  const virtualRowHeight = useMemo(() => {
    if (isModalOpen) {
      if (windowWidth < 640) return 190;
      if (windowWidth < 1024) return 150;
      if (windowWidth < 1300) return 220;
      if (windowWidth < 1536) return 200;
      return 130;
    }
    if (windowWidth < 390) return 170;
    if (windowWidth < 640) return 150;
    if (windowWidth < 1024) return 140;
    if (windowWidth < 1280) return 96;
    return 84;
  }, [isModalOpen, windowWidth]);

  if (!chosenClass || !allEmotions?.length) {
    return (
      <NoData hasStudents={filteredStudents.length > 0} hasActivity={false} />
    );
  }

  if (
    filteredDataPoints.length === 0 &&
    studentSupportData.length === 0 &&
    chosenStudent !== "All Students" &&
    chosenStudent !== "Choose Student" &&
    state.wellnessLoadedPeriods?.["2months"] === true
  ) {
    return (
      <div className="flex items-center justify-center gap-2 text-400 bg-white shadow-sm border-2 border-dotted rounded p-8 mt-3">
        <HeartPlus className="w-10 h-10 text-[#2D374840]" />
        <span className="text-[16px]">
          No wellness data for{" "}
          <span className="font-bold">
            {studentEmailToNameMap[chosenStudent] || chosenStudent}
          </span>{" "}
          in these filters. Try selecting a different time range.
        </span>
      </div>
    );
  }

  if (!state.wellnessLoadedPeriods?.["2months"]) {
    return <StudentCardSkeleton />;
  }

  const useVirtual = displayStudents.length > SMALL_LIST_THRESHOLD;
  const showHeader = !isModalOpen && displayStudents.length > 0;
  const shouldPinSelectedOutsideVirtual =
    useVirtual && isModalOpen && Boolean(selectedStudent);

  if (!useVirtual) {
    return (
      <div className="w-full mx-auto" role="list" aria-label="Student List">
        {showHeader && <StudentListHeader />}
        <div>
          {displayStudents.map((student, idx) => (
            <StudentCard
              key={student.email}
              student={student}
              isLast={idx === displayStudents.length - 1}
              isSelected={student.email === selectedStudent}
              hovered={hovered}
              onClick={(email) => handleStudentClick(email)}
              onHover={setHovered}
              togglePin={togglePin}
              isModal={isModal}
              isModalOpen={isModalOpen}
              chosenClass={chosenClass}
              formattedEmotions={formattedEmotions}
            />
          ))}
        </div>
      </div>
    );
  }

  const pinnedSelectedStudent = shouldPinSelectedOutsideVirtual
    ? displayStudents.find((s) => s.email === selectedStudent)
    : null;
  const virtualStudents =
    pinnedSelectedStudent && shouldPinSelectedOutsideVirtual
      ? displayStudents.filter((s) => s.email !== selectedStudent)
      : displayStudents;

  return (
    <div className="w-full mx-auto" role="list" aria-label="Student List">
      {showHeader && <StudentListHeader />}
      {pinnedSelectedStudent && (
        <div className="relative z-10">
          <StudentCard
            key={pinnedSelectedStudent.email}
            student={pinnedSelectedStudent}
            isLast={false}
            isSelected={true}
            hovered={hovered}
            onClick={(email) => handleStudentClick(email)}
            onHover={setHovered}
            togglePin={togglePin}
            isModal={isModal}
            isModalOpen={isModalOpen}
            chosenClass={chosenClass}
            formattedEmotions={formattedEmotions}
          />
        </div>
      )}
      <List
        rowComponent={Row}
        rowCount={virtualStudents.length}
        rowHeight={virtualRowHeight}
        rowProps={{
          itemData: {
            ...itemData,
            students: virtualStudents,
          },
        }}
        overscanCount={5}
      />
    </div>
  );
};

const Row = ({ index, itemData, style }) => {
  const {
    students,
    selectedStudent,
    hovered,
    setHovered,
    handleStudentClick,
    togglePin,
    isModal,
    isModalOpen,
    chosenClass,
    formattedEmotions,
  } = itemData;

  const student = students[index];
  if (!student) return null;
  return (
    <div style={style} className="flex">
      <div className="flex-1 min-h-full">
        <StudentCard
          key={student.email}
          student={student}
          isLast={index === students.length - 1}
          isSelected={student.email === selectedStudent}
          hovered={hovered}
          onClick={handleStudentClick}
          onHover={setHovered}
          togglePin={togglePin}
          isModal={isModal}
          isModalOpen={isModalOpen}
          chosenClass={chosenClass}
          formattedEmotions={formattedEmotions}
        />
      </div>
    </div>
  );
};

export default React.memo(StudentList, (prevProps, nextProps) =>
  isEqual(prevProps, nextProps),
);